import React from 'react';
import Select, { ValueType } from 'react-select';
import { ChevronRightIcon } from '../../../../../ui/Icons';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { UserUIModel } from '../../../../../shared/ui-models/user.ui-model';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { getSalesPersons } from '../../../../../SalesPersonsModule/data.service';
import { ReservationSource } from '../../../../../shared/enums/reservation-souce.enum';
import { ReservationChannel } from '../../../../../shared/enums/reservation-channel.enum';
import {
  channelLabels,
  getReservationChannelLabel,
  getReservationSourceImage,
  getReservationSourceLabel,
  sourceLabels
} from '../../../../../shared/services/helpers/reservation-source-channel.helper';

export type SourceChannelDraft = {
  source?: ReservationSource;
  channel?: ReservationChannel;
  salesPerson?: string;
};

type SourceOption = { value: ReservationSource; label: string };
type ChannelOption = { value: ReservationChannel; label: string };
type SalesPersonOption = {
  value: string;
  label: string;
  salesPerson: UserUIModel;
};

interface ReservationSourceChannelModalProps {
  data: {
    reservation: ReservationDetailsUiModel;
    showModal: boolean;
    sourceChannelDraft: SourceChannelDraft;
    sourceChannelSaving: boolean;
    disableSourceChannelFields: boolean;
  };
  eventHandlers: {
    onClose: () => void;
    onDraftChange: React.Dispatch<React.SetStateAction<SourceChannelDraft>>;
    onSave: () => void;
  };
}

const sourceOptions: SourceOption[] = Object.values(ReservationSource).map((source) => ({
  value: source,
  label: sourceLabels[source]
}));

const channelOption = (value: ReservationChannel): ChannelOption => ({
  value,
  label: channelLabels[value]
});

const channelOptionsBySource: Record<ReservationSource, ChannelOption[]> = {
  [ReservationSource.BN_ADMIN]: [channelOption(ReservationChannel.BIRDNEST)],
  [ReservationSource.BN_APP]: [channelOption(ReservationChannel.BIRDNEST)],
  [ReservationSource.BN_WEBSITE]: [channelOption(ReservationChannel.BIRDNEST)],
  [ReservationSource.CX]: [
    channelOption(ReservationChannel.CALL_CENTER),
    channelOption(ReservationChannel.WHATSAPP),
    channelOption(ReservationChannel.FACEBOOK),
    channelOption(ReservationChannel.INSTAGRAM),
    channelOption(ReservationChannel.REFERRAL)
  ],
  [ReservationSource.OFFLINE_SALES]: [
    channelOption(ReservationChannel.TMC),
    channelOption(ReservationChannel.CORPORATE_ACCOUNTS),
    channelOption(ReservationChannel.BROKER),
    channelOption(ReservationChannel.REFERRAL)
  ],
  [ReservationSource.ACQUISITION_TEAM]: [channelOption(ReservationChannel.ACQUISITION_TEAM)],
  [ReservationSource.PARTNERS]: [
    channelOption(ReservationChannel.BLUE_KEYS),
    channelOption(ReservationChannel.AHMED_ALI)
  ],
  [ReservationSource.CHANNEL_MANAGER]: [
    channelOption(ReservationChannel.BOOKING),
    channelOption(ReservationChannel.AIRBNB),
    channelOption(ReservationChannel.EXPEDIA),
    channelOption(ReservationChannel.VRBO)
  ]
};

const sourceChannelSelectMenuStyles = {
  control: (base: any) => ({ ...base, minHeight: 54 }),
  input: (base: any) => ({ ...base, fontSize: 18 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any) => ({ ...base, fontSize: 18, lineHeight: '28px', padding: '14px 16px' }),
  placeholder: (base: any) => ({ ...base, fontSize: 18 }),
  singleValue: (base: any) => ({ ...base, fontSize: 18 }),
  valueContainer: (base: any) => ({ ...base, padding: '8px 12px' })
};

export const ReservationSourceChannelModal = ({
  data: {
    reservation,
    showModal,
    sourceChannelDraft,
    sourceChannelSaving,
    disableSourceChannelFields
  },
  eventHandlers: { onClose, onDraftChange, onSave }
}: ReservationSourceChannelModalProps) => {
  const [salesPersons, setSalesPersons] = React.useState<UserUIModel[]>([]);
  const [isSalesPersonsLoading, setIsSalesPersonsLoading] = React.useState(false);
  const selectedSource = sourceOptions.find((option) => option.value === sourceChannelDraft.source);
  const availableChannelOptions = sourceChannelDraft.source
    ? channelOptionsBySource[sourceChannelDraft.source]
    : [];
  const selectedChannel = availableChannelOptions.find(
    (option) => option.value === sourceChannelDraft.channel
  );
  const currentSourceLabel = getReservationSourceLabel(reservation.source);
  const currentChannelLabel = getReservationChannelLabel(reservation.channel);
  const hasSourceChannelChanges =
    sourceChannelDraft.source !== reservation.source ||
    sourceChannelDraft.channel !== reservation.channel ||
    sourceChannelDraft.salesPerson !== reservation.salesPerson;
  const selectedSalesPerson = salesPersons.find(
    (salesPerson) => salesPerson.id === sourceChannelDraft.salesPerson
  );

  React.useEffect(() => {
    if (!showModal) return;

    let ignore = false;
    setIsSalesPersonsLoading(true);

    getSalesPersons()
      .then((response: UserUIModel[] | ErrorInfo) => {
        if (ignore) return;

        setSalesPersons(isErrorInfo(response) ? [] : response);
      })
      .finally(() => {
        if (!ignore) setIsSalesPersonsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [showModal]);

  return (
    <Modal
      config={{
        showModal,
        fullScreen: false,
        showClose: true,
        maxWidth: 'sm'
      }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div className="w-full rounded-xl bg-white">
        <div className="relative border-b border-blue-100 bg-gradient-to-br from-blue-50 via-white to-slate-50 px-5 py-5 pr-12 sm:px-6">
          <div className="absolute right-6 top-5 hidden size-14 rounded-full border border-blue-100 bg-white/70 shadow-sm sm:block" />
          <h2 className="m-0 text-4xl font-gilroy-medium text-gray-900">Edit source and channel</h2>
        </div>

        <div className="px-5 py-5 sm:px-6">
          <div className="mb-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <p className="m-0 text-base font-semibold uppercase tracking-wider text-gray-500">
                  Current
                </p>
              </div>

              <div className="flex items-start gap-4">
                {reservation.source && (
                  <img
                    className="size-12 rounded-xl border border-white bg-white object-contain p-2 shadow-sm"
                    src={getReservationSourceImage(reservation.source)}
                    alt={currentSourceLabel}
                  />
                )}
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-gray-400">
                      Source
                    </p>
                    <p className="mt-1 mb-0 truncate text-xl font-gilroy-medium text-gray-900">
                      {currentSourceLabel || 'No source'}
                    </p>
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-gray-400">
                      Channel
                    </p>
                    <p className="mt-1 mb-0 truncate text-lg text-gray-600">
                      {currentChannelLabel || 'No channel'}
                    </p>
                  </div>
                  {/* <div className="border-t border-gray-200 pt-3">
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-gray-400">
                      Sales person
                    </p>
                    <p className="mt-1 mb-0 truncate text-lg text-gray-600">
                      {currentSalesPersonLabel}
                    </p>
                  </div> */}
                </div>
              </div>
            </div>

            <div className="relative rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm shadow-blue-100/70">
              <div className="absolute -left-3 top-1/2 hidden size-6 -translate-y-1/2 items-center justify-center rounded-full border border-blue-100 bg-white text-blue-500 shadow-sm sm:flex">
                <ChevronRightIcon className="size-4" />
              </div>
              <div className="mb-4 flex items-center justify-between gap-3">
                <p className="m-0 text-base font-semibold uppercase tracking-wider text-blue-700">
                  New
                </p>
              </div>

              <div className="flex items-start gap-4">
                {sourceChannelDraft.source && (
                  <img
                    className="size-12 rounded-xl border border-white bg-white object-contain p-2 shadow-sm"
                    src={getReservationSourceImage(sourceChannelDraft.source)}
                    alt={selectedSource?.label || 'Selected source'}
                  />
                )}
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-blue-400">
                      Source
                    </p>
                    <p className="mt-1 mb-0 truncate text-xl font-gilroy-medium text-gray-900">
                      {selectedSource?.label || 'Select a source'}
                    </p>
                  </div>
                  <div className="border-t border-blue-100 pt-3">
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-blue-400">
                      Channel
                    </p>
                    <p className="mt-1 mb-0 truncate text-lg text-blue-700">
                      {selectedChannel?.label || 'Select a channel'}
                    </p>
                  </div>
                  {/* <div className="border-t border-blue-100 pt-3">
                    <p className="m-0 text-sm font-semibold uppercase tracking-wide text-blue-400">
                      Sales person
                    </p>
                    <p className="mt-1 mb-0 truncate text-lg text-blue-700">
                      {selectedSalesPersonLabel}
                    </p>
                  </div> */}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label
                    htmlFor="reservation-source"
                    className="block text-lg font-gilroy-medium text-gray-800"
                  >
                    Source
                  </label>
                </div>
                <Select
                  inputId="reservation-source"
                  value={selectedSource}
                  options={sourceOptions}
                  isDisabled={sourceChannelSaving || disableSourceChannelFields}
                  placeholder="Select source"
                  onChange={(value: ValueType<SourceOption, false>) => {
                    if (!value) return;

                    const nextChannelOptions = channelOptionsBySource[value.value];
                    const channel = nextChannelOptions.some(
                      (option) => option.value === sourceChannelDraft.channel
                    )
                      ? sourceChannelDraft.channel
                      : nextChannelOptions[0].value;
                    onDraftChange((draft) => ({ ...draft, source: value.value, channel }));
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={sourceChannelSelectMenuStyles}
                  aria-label="Reservation source"
                />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label
                    htmlFor="reservation-channel"
                    className="block text-lg font-gilroy-medium text-gray-800"
                  >
                    Channel
                  </label>
                </div>
                <Select
                  inputId="reservation-channel"
                  value={selectedChannel}
                  options={availableChannelOptions}
                  isDisabled={
                    sourceChannelSaving || disableSourceChannelFields || !sourceChannelDraft.source
                  }
                  placeholder="Select channel"
                  onChange={(value: ValueType<ChannelOption, false>) => {
                    if (value) {
                      onDraftChange((draft) => ({ ...draft, channel: value.value }));
                    }
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={sourceChannelSelectMenuStyles}
                  aria-label="Reservation channel"
                />
              </div>
            </div>

            <div className="mt-5">
              <label
                htmlFor="reservation-sales-person"
                className="mb-2 block text-lg font-gilroy-medium text-gray-800"
              >
                Sales person
              </label>
              <Select
                inputId="reservation-sales-person"
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={sourceChannelSelectMenuStyles}
                options={salesPersons.map((item) => ({
                  value: item.id || '',
                  label: item.name || item.email || item.phone || 'Unnamed sales person',
                  salesPerson: item
                }))}
                value={
                  selectedSalesPerson
                    ? ({
                        value: selectedSalesPerson.id || '',
                        label:
                          selectedSalesPerson.name ||
                          selectedSalesPerson.email ||
                          selectedSalesPerson.phone ||
                          'Unnamed sales person',
                        salesPerson: selectedSalesPerson
                      } as SalesPersonOption)
                    : null
                }
                onChange={(value: ValueType<SalesPersonOption, false>) => {
                  onDraftChange((draft) => ({
                    ...draft,
                    salesPerson: value?.salesPerson.email && value?.salesPerson.name
                      ? `${value.salesPerson.email} (${value.salesPerson.name})`
                      : ''
                  }));
                }}
                placeholder="Select sales person"
                isClearable={true}
                isLoading={isSalesPersonsLoading}
                isDisabled={sourceChannelSaving || isSalesPersonsLoading}
                noOptionsMessage={() =>
                  isSalesPersonsLoading ? 'Loading sales persons...' : 'No sales persons found'
                }
                aria-label="Reservation sales person"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              type={ButtonType.gray}
              size={ButtonSize.small}
              className="w-full sm:w-auto"
              onClick={onClose}
              disabled={sourceChannelSaving}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.primary}
              size={ButtonSize.small}
              className="w-full sm:w-auto"
              loading={sourceChannelSaving}
              disabled={
                !sourceChannelDraft.source ||
                !sourceChannelDraft.channel ||
                !hasSourceChannelChanges
              }
              onClick={onSave}
            >
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
